// Libraries
import { Box, Divider, IconButton, InputAdornment, Link, Typography } from "@material-ui/core";
import Visibility from "@material-ui/icons/Visibility";
import VisibilityOff from "@material-ui/icons/VisibilityOff";
import { useObserver } from "mobx-react-lite";
import React, { useState } from "react";
import { Redirect } from "react-router";

// Core
import { Loader } from "Core/Components";
import { isEmptyOrWhitespace } from "Core/Utils/Utils";

// Custom
import { StandardButton } from "Custom/Components/Buttons/StandardButton";
import { EditableInput } from "Custom/Components/Edits/EditableInput";
import { AccountPageStyles, LoginPageStyles } from "Custom/StylesAppSpecific/AccountPagesStyling";
import { LoginViewModel } from "Custom/ViewModels/LoginViewModel";

// Custom SVG Components
import { ReactComponent as Logo } from "../Content/logo.svg";

export const LoginPage: React.FunctionComponent = () => {
    // #region Code Behind

    const accountPageStyles = AccountPageStyles();
    const loginPageStyles = LoginPageStyles();

    const [viewModel] = useState(() => new LoginViewModel());
    const [showPassword, setShowPassword] = React.useState(false);

    const onClickShowPassword = () => {
        setShowPassword(!showPassword);
    };

    const onSubmit = async (event: any) => {
        event.preventDefault();

        viewModel.login();
    };

    // #endregion Code Behind

    const renderPasswordVisibilityButton = () => (
        <InputAdornment position="end" style={{ display: isEmptyOrWhitespace(viewModel.model.password) ? "none" : "flex" }}>
            <IconButton aria-label="Toggle password visibility" edge="end" onClick={onClickShowPassword} onMouseDown={onClickShowPassword}>
                {showPassword ? <Visibility /> : <VisibilityOff />}
            </IconButton>
        </InputAdornment>
    );

    const renderBusy = () => <Loader />;

    const renderError = () => (
        <React.Fragment>
            <Box className={accountPageStyles.error}>
                <Typography className={accountPageStyles.title} gutterBottom>
                    There seems to have been a problem
                </Typography>
                <Typography className={accountPageStyles.subTitle} gutterBottom>
                    {viewModel.serverValidationMessage}
                </Typography>
                <Box>
                    <Box className={`${accountPageStyles.gutterBottom} ${accountPageStyles.formControl}`}>
                        <StandardButton className={accountPageStyles.submitButton} displayName="Try again" execute={viewModel.tryAgain} />
                    </Box>
                </Box>
            </Box>
            <Divider className={accountPageStyles.divider} />
        </React.Fragment>
    );

    const renderForm = () => (
        <form onSubmit={onSubmit} className={accountPageStyles.customFormBlock}>
            <Box>
                <Box>
                    <label className={accountPageStyles.labelText}>Login</label>
                    <EditableInput
                        className={`${accountPageStyles.gutterBottom} ${accountPageStyles.formControl} ${accountPageStyles.formInputControl}`}
                        fieldName="emailAddress"
                        inputProps={{
                            placeholder: "Login in (your email address)",
                        }}
                        type="email"
                        validateOnBlur={true}
                        viewModel={viewModel}
                        data-cy="CPloginEmail"
                    />
                </Box>
                <Box>
                    <label className={accountPageStyles.labelText}>Password</label>
                    <EditableInput
                        className={`${accountPageStyles.gutterBottom} ${accountPageStyles.formControl} ${accountPageStyles.formInputControl}`}
                        fieldName="password"
                        inputProps={{
                            InputProps: {
                                endAdornment: renderPasswordVisibilityButton(),
                            },
                            placeholder: "Your password",
                        }}
                        type={showPassword ? "text" : "password"}
                        viewModel={viewModel}
                        data-cy="CPloginPassword"
                    />
                </Box>
                <Box className={`${accountPageStyles.gutterBottom} ${accountPageStyles.formControl} ${accountPageStyles.forgetPassWordRow}`}>
                    <Box></Box>
                    <Link
                        tabIndex={2}
                        className={`${accountPageStyles.linkButton} ${loginPageStyles.linkButton}`}
                        type="button"
                        component="button"
                        onClick={viewModel.navigateToForgotPassword}
                    >
                        Forgot password?
                    </Link>
                </Box>
                <Box className={`${accountPageStyles.gutterBottom} ${accountPageStyles.formControl}`}>
                    <StandardButton tabIndex={1} canExecute={!viewModel.IsLoading} className={accountPageStyles.submitButton} displayName="Log in" isSubmit={true} />
                </Box>
            </Box>
            {/* <Divider className={accountPageStyles.divider} /> */}
        </form>
    );

    return useObserver(() => {
        if (viewModel.isLoggedIn) {
            return <Redirect to={"/"} />;
        } else {
            return (
                <Box className={`${accountPageStyles.root} ${loginPageStyles.root}`}>
                    <Box className={accountPageStyles.loginPageWrapper}>
                        <Box className={accountPageStyles.loginPageFormBlock}>
                            <Box className="login-block">
                                <Box className={accountPageStyles.customerPortalImageContainer} />
                                <Logo className={accountPageStyles.mainLogo} />
                                <Box className={accountPageStyles.bodyContent}>
                                    {viewModel.canDisplayBusy && renderBusy()}
                                    {viewModel.canDisplayError && renderError()}
                                    {viewModel.canDisplayForm && renderForm()}
                                </Box>
                                <Box className={accountPageStyles.logoImageContainer} />
                            </Box>
                        </Box>
                        <Box className={accountPageStyles.houseImageContainer} />
                    </Box>
                </Box>
            );
        }
    });
};
